import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import AnimatedBackground from '../../utils/AnimatedBackground';
import Footer from '../../utils/Footer';
import TopBar from '../../utils/TopBar';
import { useExpensesPreview } from '../hooks/PreviewExpenses';

const PreviewExpenses = () => {
  const {
    previews,
    handleCellChange,
    handleSubmit,
    formatNumber,
    formatDateForInput,
    getExpenseTypes,
    availableCategories
  } = useExpensesPreview();

  const [currentPreview, setCurrentPreview] = useState(0);

  if (!previews || previews.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white bg-[#0a0a0a]">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      <AnimatedBackground />
      
      <TopBar 
        title="Preview Expenses"
        subtitle="Review your expenses before importing"
      />

      {/* Fixed Arrow Navigation */}
      <button
        onClick={() => setCurrentPreview((currentPreview - 1 + previews.length) % previews.length)}
        className="fixed left-15 top-[15%] bottom-[15%] w-20 flex items-center justify-start bg-gradient-to-r from-black/50 to-transparent text-white z-50 transition-opacity hover:opacity-80 pl-4"
        aria-label="Previous preview"
      >
        <span className="text-7xl transform transition-transform hover:scale-110">‹</span>
      </button>
      <button
        onClick={() => setCurrentPreview((currentPreview + 1) % previews.length)}
        className="fixed right-0 top-[15%] bottom-[15%] w-20 flex items-center justify-end bg-gradient-to-l from-black/50 to-transparent text-white z-50 transition-opacity hover:opacity-80 pr-4"
        aria-label="Next preview"
      >
        <span className="text-7xl transform transition-transform hover:scale-110">›</span>
      </button>

      <main className="relative z-10 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mx-auto">
            <Card className="bg-[#0f1115] border-gray-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white">Preview Expenses</CardTitle>
                  <p className="text-sm text-gray-400">
                    File {currentPreview + 1} of {previews.length}
                  </p>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table className="text-white">
                    <TableHeader>
                      <TableRow className="border-gray-800">
                        <TableHead className="text-gray-400 w-[100px]">Date</TableHead>
                        <TableHead className="text-gray-400 w-[40%]">Description</TableHead>
                        <TableHead className="text-gray-400 w-32">Category</TableHead>
                        <TableHead className="text-gray-400 w-32">Type</TableHead>
                        <TableHead className="text-gray-400 text-right w-32">Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previews[currentPreview].preview_data.map((row, rowIndex) => (
                        <TableRow key={rowIndex} className="border-gray-800 hover:bg-gray-900">
                          <TableCell className="w-[100px] px-2">
                            <div className="relative">
                              <input
                                type="date"
                                value={formatDateForInput(row.date)}
                                onChange={(e) => handleCellChange(currentPreview, rowIndex, 'date', e.target.value)}
                                className="w-full bg-transparent text-white border-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 [color-scheme:dark] text-sm"
                                style={{ colorScheme: 'dark', paddingRight: '2px' }}
                              />
                            </div>
                          </TableCell>
                          <TableCell className="w-[40%]">
                            <input
                              type="text"
                              value={row.description}
                              onChange={(e) => handleCellChange(currentPreview, rowIndex, 'description', e.target.value)}
                              className="w-full bg-transparent text-white border-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center relative">
                              <input
                                type="text"
                                value={row.category || ''}
                                onChange={(e) => handleCellChange(currentPreview, rowIndex, 'category', e.target.value)}
                                list="categories"
                                className="w-full bg-transparent text-white border-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                              <datalist id="categories">
                                {availableCategories.map((cat) => (
                                  <option key={cat} value={cat} />
                                ))}
                              </datalist>
                              {row.category && (
                                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 w-2 h-2 rounded-full bg-blue-500" 
                                  title={`Category: ${row.category}`}
                                />
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <select
                              value={row.type || 'variable expense'}
                              onChange={(e) => handleCellChange(currentPreview, rowIndex, 'type', e.target.value)}
                              className="w-full bg-transparent text-white border-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                              {getExpenseTypes().map((type) => (
                                <option key={type} value={type} className="bg-gray-900">
                                  {type}
                                </option>
                              ))}
                            </select>
                          </TableCell>
                          <TableCell>
                            <input
                              type="text"
                              value={formatNumber(row.value)}
                              onChange={(e) => handleCellChange(currentPreview, rowIndex, 'value', e.target.value)}
                              className="w-full bg-transparent text-white border-gray-700 text-right focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="border-gray-800 bg-[#0f1115]">
                        <TableCell colSpan={4} className="font-semibold text-blue-400">
                          Total Expenses
                        </TableCell>
                        <TableCell className="text-blue-400 font-semibold text-right">
                          {formatNumber(previews[currentPreview].preview_data.reduce((sum, row) => sum + (row.value || 0), 0))}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <footer className="bg-black shadow-lg border-t border-gray-800">
        <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8 flex space-x-4">
          <Link
            to="/expenses/import"
            className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600/90 hover:bg-red-700 transition-colors backdrop-blur-sm"
          >
            Back to Import
          </Link>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600/90 hover:bg-blue-700 transition-colors backdrop-blur-sm"
          >
            Confirm Import
          </button>
        </div>
        <Footer />
      </footer>
    </div>
  );
};

export default PreviewExpenses;

